/**
 * The dashboard's YubiKey pool endpoints, typed — a sibling of `locations/api.ts`, not a part of
 * it. `yubikeys.location_id` is nullable in the database, so the pool is fleet-wide: a key can sit
 * unassigned in a warehouse, and "how many keys do we own and what state are they in" is a question
 * no per-location screen can answer. See `packages/contracts/src/yubikeys.ts` for the same reasoning
 * on the server side.
 */
import {
  deleteResultSchema,
  yubikeyListSchema,
  yubikeySchema,
  type CreateYubikey,
  type DeleteResult,
  type UpdateYubikey,
  type Yubikey,
  type YubikeyList,
} from '@apuestapr/contracts';

import type { ApiClient, ApiResult } from '../api/client.ts';

export const yubikeysPath = '/api/admin/yubikeys';
const yubikeyPath = (id: number): string => `${yubikeysPath}/${String(id)}`;

export interface YubikeysApi {
  readonly list: () => Promise<ApiResult<YubikeyList>>;
  readonly create: (input: CreateYubikey, csrfToken: string) => Promise<ApiResult<Yubikey>>;
  readonly update: (
    id: number,
    input: UpdateYubikey,
    csrfToken: string,
  ) => Promise<ApiResult<Yubikey>>;
  readonly remove: (id: number, csrfToken: string) => Promise<ApiResult<DeleteResult>>;
}

export const createYubikeysApi = (client: ApiClient): YubikeysApi => ({
  list: () => client.get(yubikeysPath, yubikeyListSchema),
  create: (input, csrfToken) =>
    client.send('POST', yubikeysPath, yubikeySchema, { body: input, csrfToken }),
  update: (id, input, csrfToken) =>
    client.send('PATCH', yubikeyPath(id), yubikeySchema, { body: input, csrfToken }),
  remove: (id, csrfToken) =>
    client.send('DELETE', yubikeyPath(id), deleteResultSchema, { csrfToken }),
});
